import * as df from "date-fns";
import type {
  SessionStatus,
  SessionWithOwnerDogDisplayListOutput,
  SessionWithOwnerDogOutput,
  SessionWithTrainerDisplayListOutput,
  SessionWithTrainerOutput,
} from "@/api-types";
export type SessionWithTrainer =
  | SessionWithTrainerOutput
  | SessionWithTrainerDisplayListOutput;
export type SessionWithOwner =
  | SessionWithOwnerDogOutput
  | SessionWithOwnerDogDisplayListOutput;
export type SessionUnionType = SessionWithTrainer | SessionWithOwner;

export type { SessionStatus };

export const _ActiveSessionStatuses = [
  "REQUESTED",
  "CONFIRMED",
  "COMPLETED",
] as const;
export const ActiveSessionStatuses: string[] = Array.from(
  _ActiveSessionStatuses,
);

const _CancelledSessionStatuses = [
  "CANCELLED_BY_OWNER",
  "CANCELLED_BY_TRAINER",
  "DECLINED_BY_TRAINER",
  "DECLINED_BY_OWNER",
  "REQUEST_EXPIRED",
] as const;
export const CancelledSessionStatuses: string[] = Array.from(
  _CancelledSessionStatuses,
);

export const FutureSessionStatuses = ["REQUESTED", "CONFIRMED"] as const;

export const PastSessionStatuses = [
  "COMPLETED",
  ...CancelledSessionStatuses,
] as const;

export const groupBySessions = <
  SessionType extends SessionUnionType,
  GroupKey extends string,
>(
  sessions: SessionType[],
  statusGroup: Record<GroupKey, string[]>,
): Record<GroupKey, SessionType[]> => {
  const result = {} as Record<GroupKey, SessionType[]>;

  // Initialize empty arrays for each group
  Object.keys(statusGroup).forEach((groupKey) => {
    result[groupKey as GroupKey] = [];
  });

  // Group sessions by merged statuses
  sessions.forEach((session) => {
    (Object.entries(statusGroup) as [GroupKey, string[]][]).forEach(
      ([groupKey, statuses]) => {
        if (statuses.includes(session.status)) {
          result[groupKey as GroupKey].push(session);
        }
      },
    );
  });

  // Sort sessions in each group by sorting_timestamp
  (Object.values(result) as SessionType[][]).forEach(
    (sessionGroup: SessionType[]) => {
      sessionGroup.sort((a, b) =>
        df.compareAsc(a.sortingTimestamp, b.sortingTimestamp),
      );
    },
  );

  return result;
};

/**
 * Compares two chat messages by their sentAt timestamp for sorting
 * @param a - First chat message
 * @param b - Second chat message
 * @returns Negative if a is newer, positive if b is newer, 0 if equal
 */
export const compareMessagesByDate = (
  a: { lastMessage?: { sentAt: string } | null },
  b: { lastMessage?: { sentAt: string } | null },
): number => {
  const aTime = new Date(a.lastMessage?.sentAt ?? 0).getTime();
  const bTime = new Date(b.lastMessage?.sentAt ?? 0).getTime();
  return bTime - aTime; // Sort newest first
};
